import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { useVeCusto } from '../../design/sigilo';
import { reais } from '../../produtos';
import { diaMes, lerCalendario } from '../../validades';
import type { Calendario, FaixaDeVencimento } from '../../validades';
import { ErroDaLista, Esqueleto } from '../equipe/Equipe';
import type { Periodo } from '../../paineis';
import { DrawerDeLotes } from './DrawerDeLotes';

/**
 * TELA 26-A — CENTRAL DE VALIDADES. "Alerta existe para levar a uma ação."
 *
 * Faixas no topo como filtro, lista de lotes no centro (a lista vem ANTES do gráfico: o gestor
 * precisa agir, não admirar) e o calendário do mês à direita. Clicar num dia filtra a lista —
 * sem abrir outra página.
 */

const FAIXAS: readonly (readonly [FaixaDeVencimento, string, string?])[] = [
  ['vencidos', 'VENCIDOS', 'alerta'],
  ['hoje', 'HOJE', 'alerta'],
  ['ate7', 'ATÉ 7 DIAS', 'aviso'],
  ['de8a30', '8–30 DIAS'],
  ['de31a60', '31–60 DIAS'],
  ['de61a90', '61–90 DIAS'],
  ['sem_validade', 'SEM VALIDADE'],
];

const TITULO_DA_LISTA: Record<FaixaDeVencimento, string> = {
  vencidos: 'Lotes vencidos',
  hoje: 'Lotes que vencem hoje',
  ate7: 'Lotes que vencem até 7 dias',
  de8a30: 'Lotes que vencem de 8 a 30 dias',
  de31a60: 'Lotes que vencem de 31 a 60 dias',
  de61a90: 'Lotes que vencem de 61 a 90 dias',
  sem_validade: 'Lotes sem validade informada',
};

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function Vencimentos({
  sessao,
  configura,
  periodo = null,
}: {
  sessao: Sessao;
  configura: boolean;
  /**
   * O filtro de período do módulo (dono, 05/10), pela VALIDADE: a lista mostra o que vence nele.
   * null = as faixas de sempre.
   */
  periodo?: Periodo | null;
}) {
  const veCusto = useVeCusto();
  const [faixa, setFaixa] = useState<FaixaDeVencimento>('ate7');
  const [mes, setMes] = useState(() =>
    (periodo ?? { de: new Date().toISOString() }).de.slice(0, 7),
  );
  const [dia, setDia] = useState<string | null>(null);
  // Escolheu um período: o calendário vai para o mês em que ele começa.
  useEffect(() => {
    if (!periodo) return;
    setMes(periodo.de.slice(0, 7));
    setDia(null);
  }, [periodo?.de, periodo?.ate]);
  const [cal, setCal] = useState<Calendario | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const r = await lerCalendario({ faixa, mes, dia, periodo });
    if (!r.ok) return setFalhou(true);
    setFalhou(false);
    setCal(r.dados);
  }, [faixa, mes, dia, periodo?.de, periodo?.ate]);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  const [ano = 0, m = 1] = mes.split('-').map(Number);
  const nomeDoMes = `${MESES[m - 1] ?? ''} ${String(ano)}`;
  const mudarMes = (d: number) => {
    const t = new Date(Date.UTC(ano, m - 1 + d, 1));
    setMes(t.toISOString().slice(0, 7));
    setDia(null);
  };

  return (
    <div className="ac pv-venc">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Calendário de vencimentos</h1>
          <div className="ac-subtitulo">
            {cal
              ? `${cal.totalAcompanhados.toLocaleString('pt-BR')} lotes acompanhados${
                  veCusto
                    ? ` · valor a custo dentro da janela: ${
                        cal.valorACustoNaJanela ? reais(cal.valorACustoNaJanela) : 'indisponível'
                      }`
                    : ''
                }`
              : 'Carregando…'}
          </div>
        </div>
        <div className="ac-acoes pv-mes">
          <button
            type="button"
            className="ac-botao"
            aria-label="Mês anterior"
            onClick={() => mudarMes(-1)}
          >
            <Icone nome="chev-r" tamanho={14} traco={1.8} />
          </button>
          <span className="pv-mes-nome">{nomeDoMes}</span>
          <button
            type="button"
            className="ac-botao"
            aria-label="Próximo mês"
            onClick={() => mudarMes(1)}
          >
            <Icone nome="chev-r" tamanho={14} traco={1.8} />
          </button>
        </div>
      </div>

      <div className="pv-faixas">
        {FAIXAS.map(([id, rotulo, tom]) => {
          const f = cal?.faixas[id];
          const ativo = f !== undefined && f.lotes > 0;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={faixa === id && !dia && !periodo}
              className={`ac-kpi pv-faixa${faixa === id && !dia && !periodo ? ' pv-faixa-ativa' : ''}${ativo && tom === 'alerta' ? ' ac-kpi-alerta' : ''}${ativo && tom === 'aviso' ? ' ac-kpi-aviso' : ''}`}
              onClick={() => {
                setFaixa(id);
                setDia(null);
                // A faixa manda de novo: sai do período (o endereço fica sem ele).
                if (periodo) window.location.hash = window.location.hash.split('?')[0] ?? '';
              }}
            >
              <div className="ac-kpi-rotulo">{rotulo}</div>
              <div className="ac-kpi-valor">{f ? f.lotes : '—'}</div>
              <div className="pd-kpi-sub">
                {id === 'sem_validade' ? 'não informada' : f ? `${f.unidades} un` : ' '}
              </div>
            </button>
          );
        })}
      </div>

      {falhou && (
        <ErroDaLista
          titulo="Não foi possível carregar os vencimentos"
          aoTentar={() => void carregar()}
        />
      )}

      <div className="pv-venc-corpo">
        <div className="ac-cartao pv-venc-lista">
          <div className="pf-matriz-cabeca">
            <div className="pf-matriz-titulo">
              {dia
                ? `Lotes que vencem em ${diaMes(dia)}`
                : periodo
                  ? `Lotes que vencem de ${diaMes(periodo.de)} a ${diaMes(periodo.ate)}`
                  : TITULO_DA_LISTA[faixa]}
            </div>
            <span className="ac-espaco" />
            {dia ? (
              <button type="button" className="ac-link" onClick={() => setDia(null)}>
                {periodo ? 'Voltar para o período' : 'Voltar para a faixa'}
              </button>
            ) : (
              <span className="ac-nota">ordenado por dias restantes</span>
            )}
          </div>
          {!cal && !falhou && <Esqueleto linhas={5} />}
          {cal && cal.lotes.length === 0 && (
            <div className="ac-vazio">
              <div className="ac-vazio-titulo">Nenhum vencimento neste período</div>
              <div className="ac-vazio-texto">
                {periodo
                  ? 'Nenhum lote ativo com saldo vence neste período.'
                  : 'Nenhum lote ativo com saldo nesta faixa.'}
              </div>
            </div>
          )}
          {cal && cal.lotes.length > 0 && (
            <div className="ac-tabela-rolo">
              <table className="ac-tabela">
                <thead>
                  <tr>
                    <th>DIA</th>
                    <th>PRODUTO · LOTE</th>
                    <th className="ac-direita">DIAS</th>
                    <th className="ac-direita">QTD</th>
                    {veCusto && <th className="ac-direita">CUSTO</th>}
                    <th className="ac-direita">AÇÃO</th>
                  </tr>
                </thead>
                <tbody>
                  {cal.lotes.map((l) => (
                    <tr key={l.loteId}>
                      <td className="ac-mono">{l.validade ? diaMes(l.validade) : '—'}</td>
                      <td>
                        <div className="ac-pessoa-nome">{l.descricao}</div>
                        <div className="pd-codigos">
                          {[l.codigo, l.codigoProduto].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td
                        className={`ac-direita pv-dias pv-dias-${l.dias !== null && l.dias <= 0 ? 'erro' : 'aviso'}`}
                      >
                        {l.dias === null
                          ? '—'
                          : l.dias === 0
                            ? 'hoje'
                            : String(l.dias).replace('-', '−')}
                      </td>
                      <td className="ac-direita ac-num">{l.quantidade ?? 'indisponível'}</td>
                      {veCusto && (
                        <td className="ac-direita ac-num">
                          {l.valorACusto ? reais(l.valorACusto) : 'indisponível'}
                        </td>
                      )}
                      <td className="ac-direita">
                        <button
                          type="button"
                          className="ac-link"
                          onClick={() => setAberto(l.productId)}
                        >
                          Ver lote
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="ac-rodape-tabela">
            <span>
              {cal
                ? `${String(cal.lotes.length)} ${cal.lotes.length === 1 ? 'lote' : 'lotes'}`
                : ' '}
            </span>
            <span>a lista vem antes do gráfico: o gestor precisa agir</span>
          </div>
        </div>

        <Mes
          ano={ano}
          mes={m}
          hoje={cal?.hoje ?? null}
          porDia={cal?.porDia ?? []}
          selecionado={dia}
          nome={MESES[m - 1] ?? ''}
          aoEscolher={(d) => setDia(d === dia ? null : d)}
        />
      </div>

      {aberto && (
        <DrawerDeLotes
          productId={aberto}
          sessao={sessao}
          configura={configura}
          aoFechar={() => setAberto(null)}
          aoMudar={() => void carregar()}
        />
      )}
    </div>
  );
}

function Mes({
  ano,
  mes,
  hoje,
  porDia,
  selecionado,
  nome,
  aoEscolher,
}: {
  ano: number;
  mes: number;
  hoje: string | null;
  porDia: readonly { dia: string; lotes: number }[];
  selecionado: string | null;
  nome: string;
  aoEscolher: (dia: string) => void;
}) {
  const celulas = useMemo(() => {
    const primeiro = new Date(Date.UTC(ano, mes - 1, 1));
    const dias = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
    const vazio = primeiro.getUTCDay();
    const mapa = new Map(porDia.map((d) => [d.dia, d.lotes]));
    const lista: ({ dia: string; n: number; lotes: number } | null)[] = Array.from(
      { length: vazio },
      () => null,
    );
    for (let d = 1; d <= dias; d += 1) {
      const iso = `${String(ano)}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      lista.push({ dia: iso, n: d, lotes: mapa.get(iso) ?? 0 });
    }
    return lista;
  }, [ano, mes, porDia]);

  return (
    <div className="ac-cartao pv-calendario">
      <div className="pf-matriz-cabeca">
        <div className="pf-matriz-titulo">{nome}</div>
        <span className="ac-espaco" />
        <span className="ac-nota">lotes por dia</span>
      </div>
      <div className="pv-cal-grade">
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
          <div key={i} className="pv-cal-semana">
            {d}
          </div>
        ))}
        {celulas.map((c, i) =>
          c ? (
            <button
              key={c.dia}
              type="button"
              disabled={c.lotes === 0}
              aria-pressed={selecionado === c.dia}
              className={`pv-cal-dia${c.lotes ? ' pv-cal-com' : ''}${selecionado === c.dia ? ' pv-cal-sel' : ''}${hoje === c.dia ? ' pv-cal-hoje' : ''}`}
              onClick={() => aoEscolher(c.dia)}
              aria-label={`${String(c.n)}: ${String(c.lotes)} ${c.lotes === 1 ? 'lote' : 'lotes'}`}
            >
              <span>{c.n}</span>
              {c.lotes > 0 && <b>{c.lotes}</b>}
            </button>
          ) : (
            <div key={`v${String(i)}`} />
          ),
        )}
      </div>
      <div className="pv-cal-pe">
        Cada dia mostra a contagem de lotes. Clicar filtra a lista ao lado.
      </div>
    </div>
  );
}
